import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { BOOKS } from "@/data/content";

export default function Books() {
  return (
    <section
      id="books"
      className="relative py-28 md:py-36"
      data-testid="books-section"
    >
      <div className="section-x mx-auto max-w-[1400px]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="section-eyebrow">02 — Book Published</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
              Practical Java, <br className="hidden md:block" />
              <span className="text-ink/50">loved on Amazon.</span>
            </h2>
          </div>
          <p className="text-ink/55 max-w-sm text-[15px]">
            Two books. One goal — turn Java from a language into a QA
            superpower.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {BOOKS.map((b, i) => (
            <BookCard book={b} index={i} key={b.title} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BookCard({ book, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const isBest = book.badge === "Best Seller";

  return (
    <motion.a
      ref={ref}
      href={book.url}
      target="_blank"
      rel="noreferrer"
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      className="group relative overflow-hidden rounded-3xl glass p-6 md:p-8 hover:-translate-y-1 hover:border-ink/20 transition-[transform,border-color] duration-300"
      data-testid={`book-card-${index}`}
    >
      {isBest && <span className="absolute inset-0 ring-conic rounded-3xl -z-0" />}
      <div className="relative z-10 grid grid-cols-[140px_1fr] md:grid-cols-[180px_1fr] gap-6 items-start">
        <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-ink/10 bg-ink-2">
          <img
            src={book.cover}
            alt={`Cover of ${book.title}`}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        </div>
        <div>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.24em] ${
              isBest
                ? "border-accent-cyan/40 bg-accent-cyan/10 text-accent-cyan"
                : "border-accent-purple/40 bg-accent-purple/10 text-accent-purple"
            }`}
          >
            <BookOpen size={11} /> {book.badge}
          </span>
          <h3 className="mt-4 font-display text-2xl md:text-[28px] leading-tight tracking-tight">
            {book.title}
          </h3>
          <p className="mt-2 text-ink/55 text-sm md:text-[15px] leading-relaxed max-w-sm">
            {book.subtitle}
          </p>
          <span
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-ink/[0.04] px-4 py-2 text-xs font-semibold text-ink group-hover:border-accent-cyan/60 group-hover:bg-accent-cyan/10 transition-colors"
            data-testid={`book-cta-${index}`}
          >
            Buy on Amazon
            <ArrowUpRight
              size={13}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </span>
        </div>
      </div>
    </motion.a>
  );
}
